import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearRouterChoice,
  setDIAIPAllocation,
  setRouterChoice,
  setSecondaryIPOption,
} from 'Quotes/actions';
import { selectQuote } from 'Quotes/selectors';
import { DualCircuitRouterConfigurations, IPAddressAllocation } from './types/diaIPAllocation';
import { Option, OptionGroup } from 'shared/components/organisms/OptionGroup/OptionGroup';
import { IQuote } from 'Quotes/types/store';

export const diaRouterOptions: Option<DualCircuitRouterConfigurations>[] = [
  { 
    label: 'BGP', 
    value: DualCircuitRouterConfigurations.DUAL_FAILOVER_ACTIVE_ACTIVE,
    tooltip: '2 resilient circuits configured using BGP. Each circuit will be allocated a /31 address for the WAN, a routed subnet will then be provided for use across both services along with a private AS allocation to enable BGP configuration. Customer is responsible for any routing failover between the 2 circuits. For any other configuration requirements, please contact your account manager.'
  },
  { 
    label: 'Independent', 
    value: DualCircuitRouterConfigurations.DUAL_INDEPENDENT,
    tooltip: '2 resilient circuits with individual IP address ranges assigned to each. Customer is responsible for any routing failover between the 2 circuits.'
  },
];

export const mdiaRouterOptions: Option<DualCircuitRouterConfigurations>[] = [
  { 
    label: 'Active/Active', 
    value: DualCircuitRouterConfigurations.DUAL_FAILOVER_ACTIVE_ACTIVE,
    tooltip: '2 resilient circuits configured using BGP. Each circuit will be allocated a /31 address for the WAN, a routed subnet will then be provided for use across both services along with a private AS allocation to enable BGP configuration. Customer is responsible for any routing failover between the 2 circuits. For any other configuration requirements, please contact your account manager.'
  },
  { 
    label: 'Active/Passive', 
    value: DualCircuitRouterConfigurations.DUAL_FAILOVER_ACTIVE_STANDBY,
    tooltip: '2 resilient circuits with individual IP address ranges assigned to each. Customer is responsible for any routing failover between the 2 circuits.'
  },
  // { 
  //   label: 'Independent', 
  //   value: DualCircuitRouterConfigurations.DUAL_INDEPENDENT 
  // },
];

/**
 * Returns `true` if RO2 enabled and Router = Independent.
 * Returns `false` if RO2 enabled and Router != Independent.
 * Returns `undefined` if RO2 is not enabled or no Router choice is made.
 */
export const selectIsIndependentRouter = (quote: IQuote): boolean | undefined => {
  if (!quote.location.aEnd.routerChoice) return undefined;
  return quote.location.aEnd.routerChoice === DualCircuitRouterConfigurations.DUAL_INDEPENDENT;
};

/**
 * DUAL_FAILOVER_ACTIVE_ACTIVE = BGP, 
 * DUAL_INDEPENDENT = Independent
 */
export const mapLabelToRouterChoice = (routerChoice: DualCircuitRouterConfigurations, is_managed_dia: boolean): string => {
  const options = is_managed_dia ? mdiaRouterOptions : diaRouterOptions;
  const option = options.find((option) => option.value === routerChoice);
  if (option) return option.label;
  return "";
}
/**
 * DUAL_FAILOVER_ACTIVE_ACTIVE = BGP, 
 * DUAL_INDEPENDENT = Independent
 */
export const mapRouterChoiceToLabel = (routerChoice?: DualCircuitRouterConfigurations): string | undefined => {
  const option = diaRouterOptions.find((option) => option.value === routerChoice);
  return option ? option.label : undefined;
}

export const RoutingOption = ({ readonly }: { readonly?: boolean }) => {
  const dispatch = useDispatch();
  const quote = useSelector(selectQuote);
  const isManagedDIA = quote.location.aEnd.is_managed_dia;
  const routerOptions = isManagedDIA ? mdiaRouterOptions : diaRouterOptions;

  React.useEffect(() => {
    const currentRouterChoice = quote.location.aEnd.routerChoice;
    if (!currentRouterChoice) return;

    const isCurrentChoiceValid = routerOptions.some((option) => option.value === currentRouterChoice);
    if (!isCurrentChoiceValid) {
      dispatch(clearRouterChoice());
      dispatch(setSecondaryIPOption(undefined));
    }
  }, [dispatch, quote.location.aEnd.routerChoice, routerOptions]);

  const setRouter = (router?: DualCircuitRouterConfigurations) => {
    if (router === undefined) {
      dispatch(clearRouterChoice());
    } else if (quote.location.aEnd.routerChoice !== router) {
      dispatch(setRouterChoice(router));
    }

    if (isManagedDIA === false && router === DualCircuitRouterConfigurations.DUAL_FAILOVER_ACTIVE_ACTIVE) {
      dispatch(setDIAIPAllocation(IPAddressAllocation.WAN_31_PUBLIC_ALLOCATION));
    }

    if (router !== DualCircuitRouterConfigurations.DUAL_INDEPENDENT && quote.location.aEnd.secondIPChoice !== undefined) {
      dispatch(setSecondaryIPOption(undefined));
    } else if (router === DualCircuitRouterConfigurations.DUAL_INDEPENDENT && quote.location.aEnd.secondIPChoice !== quote.location.aEnd.ip?.selectedId) {
      dispatch(setSecondaryIPOption(quote.location.aEnd.ip?.selectedId ?? undefined));
    }
  };

  return (
    <div className='routing-option-section' data-testid="routing-option-section">
      <p className="mt-4 mb-2 addon_options__title">Routing Option</p>

      <OptionGroup
        options={routerOptions}
        onClick={(routerChoice) => setRouter(routerChoice)}
        value={quote.location.aEnd.routerChoice}
        readOnly={readonly}
        itemsPerRow={3}
      />
    </div>
  );
};